import { Checkbox, Chip, TableCell, TableRow, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { IdField } from '~/components/Fields';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { getModalityIcon } from '~/domain/dicom/modality';
import { SeriesData } from '~/utils/transforms/patientDataTransforms';
import { SelectedDatasets } from '.';
import { ActiveItem } from '../../useActiveItems';

type DatasetRowProps = {
    row: SeriesData;
    selectedDatasets: SelectedDatasets;
    activeItems: ActiveItem[];
    onCheckSelect: (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        serie: SeriesData
    ) => void;
};

export default function PatientDatasetRow({
    row,
    selectedDatasets,
    onCheckSelect,
    activeItems,
}: DatasetRowProps) {
    const isActive = activeItems.some((item) => item.dicomUid === row.id);
    const isSelected = selectedDatasets[row.modality]
        ? Object.keys(selectedDatasets[row.modality]).some(
              (seriesId) => seriesId === row.id
          )
        : false;

    return (
        <TableRow
            key={row.id}
            hover
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={-1}
            selected={isSelected}
            sx={(theme) => ({
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
                ...(isActive && {
                    backgroundColor: 'rgba(0, 165, 185, 0.05)',
                }),
            })}
            onClick={(event) => onCheckSelect(event, row)}
        >
            <TableCell
                sx={{
                    width: '10px',
                    py: 0.5,
                    px: 1,
                }}
                onClick={(event) => {
                    event.stopPropagation();
                    onCheckSelect(event, row);
                }}
            >
                <Checkbox
                    disableRipple
                    size="small"
                    color="primary"
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                    }}
                    sx={{
                        '&': {
                            padding: 0,
                        },
                    }}
                />
            </TableCell>

            <TableCell sx={{ py: 0.5, px: 1 }}>
                <Typography
                    display="flex"
                    alignItems="center"
                    gap={1}
                    color="primary"
                    fontSize={14}
                >
                    {getModalityIcon(row.modality)} {row.modality}
                </Typography>
            </TableCell>
            <TableCell
                sx={{
                    py: 0.5,
                    px: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: 14,
                }}
            >
                {row.description}
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, opacity: 0.7 }}>
                {row.seriesDate?.toDateString()}
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, opacity: 0.7 }}>
                <SensitiveInfoTypography sx={{ fontSize: 12 }}>
                    <IdField id={row.id} />
                </SensitiveInfoTypography>
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                {isActive && (
                    <Chip
                        variant="outlined"
                        size="small"
                        sx={{
                            fontSize: 10,
                            height: 20,
                            padding: 0.5,
                            lineHeight: 0.9,
                        }}
                        color="success"
                        label={<FormattedMessage id="active" />}
                    />
                )}
            </TableCell>
        </TableRow>
    );
}
